import { useForm, Controller } from "react-hook-form";
import { Input } from "@/components/base/input/input";
import { TextArea } from "@/components/base/textarea";
import { Button } from "@/components/base/buttons/button";
import type { CreateCustomerPayload } from "@/api/customers";

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  notes: string;
}

interface CustomerFormProps {
  defaultValues?: Partial<{
    firstName: string;
    lastName: string;
    email: string | null;
    phone: string | null;
    address: { street?: string; city?: string; state?: string; zip?: string } | null;
    notes: string | null;
  }>;
  onSubmit: (data: CreateCustomerPayload) => void;
  onCancel: () => void;
  isLoading: boolean;
}

export default function CustomerForm({
  defaultValues,
  onSubmit,
  onCancel,
  isLoading,
}: CustomerFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      firstName: defaultValues?.firstName ?? "",
      lastName: defaultValues?.lastName ?? "",
      email: defaultValues?.email ?? "",
      phone: defaultValues?.phone ?? "",
      street: defaultValues?.address?.street ?? "",
      city: defaultValues?.address?.city ?? "",
      state: defaultValues?.address?.state ?? "",
      zip: defaultValues?.address?.zip ?? "",
      notes: defaultValues?.notes ?? "",
    },
  });

  const onFormSubmit = (values: FormValues) => {
    const hasAddress = values.street || values.city || values.state || values.zip;
    onSubmit({
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email || null,
      phone: values.phone || null,
      address: hasAddress
        ? {
            street: values.street || undefined,
            city: values.city || undefined,
            state: values.state || undefined,
            zip: values.zip || undefined,
          }
        : null,
      notes: values.notes || null,
    });
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Controller
          name="firstName"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              label="First Name"
              placeholder="First name"
              isRequired
              isInvalid={!!errors.firstName}
              hint={errors.firstName ? "First name is required" : undefined}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          name="lastName"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              label="Last Name"
              placeholder="Last name"
              isRequired
              isInvalid={!!errors.lastName}
              hint={errors.lastName ? "Last name is required" : undefined}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Input
              label="Email"
              placeholder="email@example.com"
              type="email"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <Input
              label="Phone"
              placeholder="555-000-0000"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Controller
          name="street"
          control={control}
          render={({ field }) => (
            <Input
              label="Street"
              placeholder="123 Main St"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          name="city"
          control={control}
          render={({ field }) => (
            <Input
              label="City"
              placeholder="City"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Controller
          name="state"
          control={control}
          render={({ field }) => (
            <Input
              label="State"
              placeholder="IL"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          name="zip"
          control={control}
          render={({ field }) => (
            <Input
              label="ZIP"
              placeholder="62701"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      <Controller
        name="notes"
        control={control}
        render={({ field }) => (
          <TextArea
            label="Notes"
            placeholder="Optional notes about this customer..."
            rows={3}
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />

      <div className="flex justify-end gap-3 pt-2">
        <Button
          type="button"
          color="secondary"
          size="md"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          color="primary"
          size="md"
          isLoading={isLoading}
          isDisabled={isLoading}
        >
          {defaultValues ? "Save Changes" : "Add Customer"}
        </Button>
      </div>
    </form>
  );
}
